import { create } from 'zustand'

const useSessionStore = create((set, get) => ({
  // Current Session State
  currentQuestionIndex: 0,
  combo: 0,
  vibeGauge: 0,
  sessionScore: 0,
  mistakes: 0,
  correctAnswers: 0,
  incorrectAnswers: 0,
  timeRemaining: 30,
  sessionStartTime: null,
  sessionEndTime: null,

  // Question State
  currentAnswers: [], // User's selected answers for current question
  isAnswerCorrect: null,
  showFeedback: false,

  // Effects
  currentEffect: 'normal', // normal, nice, great, amazing, fever
  isFeverActive: false,

  // Actions
  startSession: () => set({
    currentQuestionIndex: 0,
    combo: 0,
    vibeGauge: 0,
    sessionScore: 0,
    mistakes: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    currentAnswers: [],
    isAnswerCorrect: null,
    showFeedback: false,
    sessionStartTime: Date.now(),
    sessionEndTime: null,
    currentEffect: 'normal',
    isFeverActive: false,
  }),

  nextQuestion: () => set((state) => ({
    currentQuestionIndex: state.currentQuestionIndex + 1,
    currentAnswers: [],
    isAnswerCorrect: null,
    showFeedback: false,
    timeRemaining: 30,
  })),

  selectAnswer: (answer) => set((state) => ({
    currentAnswers: [...state.currentAnswers, answer],
  })),

  removeLastAnswer: () => set((state) => ({
    currentAnswers: state.currentAnswers.slice(0, -1),
  })),

  clearAnswers: () => set({
    currentAnswers: [],
  }),

  submitAnswer: (isCorrect, points, timeBonus) => {
    const state = get()

    if (isCorrect) {
      const newCombo = state.combo + 1
      const comboBonus = newCombo * 10
      const totalPoints = points + comboBonus + timeBonus
      const newVibe = Math.min(100, state.vibeGauge + 5)

      // Determine effect based on combo
      let effect = 'normal'
      if (newCombo >= 30) effect = 'fever'
      else if (newCombo >= 20) effect = 'amazing'
      else if (newCombo >= 10) effect = 'great'
      else if (newCombo >= 5) effect = 'nice'

      set({
        combo: newCombo,
        vibeGauge: newVibe,
        sessionScore: state.sessionScore + totalPoints,
        correctAnswers: state.correctAnswers + 1,
        isAnswerCorrect: true,
        showFeedback: true,
        currentEffect: effect,
        isFeverActive: newVibe >= 76,
      })
    } else {
      const newVibe = Math.max(0, state.vibeGauge - 10)

      set({
        combo: 0,
        vibeGauge: newVibe,
        mistakes: state.mistakes + 1,
        incorrectAnswers: state.incorrectAnswers + 1,
        isAnswerCorrect: false,
        showFeedback: true,
        currentEffect: 'normal',
        isFeverActive: newVibe >= 76,
      })
    }
  },

  hideFeedback: () => set({
    showFeedback: false,
  }),

  updateTimeRemaining: (time) => set({
    timeRemaining: time,
  }),

  endSession: () => set({
    sessionEndTime: Date.now(),
  }),

  // Reset session
  resetSession: () => set({
    currentQuestionIndex: 0,
    combo: 0,
    vibeGauge: 0,
    sessionScore: 0,
    mistakes: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    currentAnswers: [],
    isAnswerCorrect: null,
    showFeedback: false,
    timeRemaining: 30,
    sessionStartTime: null,
    sessionEndTime: null,
    currentEffect: 'normal',
    isFeverActive: false,
  }),

  // Get session summary
  getSessionSummary: () => {
    const state = get()
    const duration = state.sessionEndTime
      ? (state.sessionEndTime - state.sessionStartTime) / 1000
      : 0

    const totalQuestions = state.correctAnswers + state.incorrectAnswers
    const accuracy = totalQuestions > 0
      ? Math.round((state.correctAnswers / totalQuestions) * 100)
      : 0

    return {
      score: state.sessionScore,
      combo: state.combo,
      correctAnswers: state.correctAnswers,
      incorrectAnswers: state.incorrectAnswers,
      totalQuestions,
      mistakes: state.mistakes,
      accuracy,
      duration: Math.round(duration),
    }
  },
}))

export default useSessionStore
